import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Validation from "../../../Validations/FormValidations";
import { GetDepReserveAmount, GetReserveAmount } from "../API/RequestsServices";

const RequestForm = (props) => {
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);

  // focus trap entry
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  function nextMidnightUtcIso(dateOnly /* "yyyy-MM-dd" */) {
    if (!dateOnly) return null;
    const [y, m, d] = dateOnly.split("-").map(Number);
    const localMidnight = new Date(y, m - 1, d, 0, 0, 0, 0);
    const nextLocalMidnight = new Date(localMidnight);
    nextLocalMidnight.setDate(nextLocalMidnight.getDate() + 1);
    const utcMillis =
      nextLocalMidnight.getTime() - nextLocalMidnight.getTimezoneOffset() * 60000;
    return new Date(utcMillis).toISOString();
  }

  const CreateOrGetCustomer = async () => {
    const trimString = (v) => (typeof v === "string" ? v.trim() : v);
    const url = process.env.REACT_APP_PAYMENT_API + "Customer/CreateCustomer";
    const data = {
      FirstName: props.CustomerDetails.FirstName,
      LastName: props.CustomerDetails.lastName,
      MiddleName: props.CustomerDetails.middleName ?? "",
      Email: props.CustomerDetails.email ?? "",
      PhoneNumber: props.CustomerDetails.phone,
      CountryCode: "973",
      Description: "",
      Extra1: props.CustomerDetails.code ?? "",
      Extra2: "",
      CPR: trimString(props.CustomerDetails.cpr),
      Nullable: props.selectedOption === "ActivePolicyRecurring",
    };

    try {
      props.setFormLoading?.(true);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to create / get customer");
      const result = await response.json();
      return result;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const SendRequest = async () => {
    let errorMsg = "";
    const url = process.env.REACT_APP_PAYMENT_API + "Requests/SendRequest";

    const CustomersPayments = { CPR: props.CustomerDetails.cpr };
    const SolidarityUsers = { AuthToken: sessionStorage.getItem("token") };

    const expIso = nextMidnightUtcIso(props.CustomerDetails.expiry);
    const trimString = (v) => (typeof v === "string" ? v.trim() : v);

    const data = {
      Amount: props.PolicyDetails.TotalAmount,
      QuoteNumber:
        props.selectedOption === "General Payment"
          ? trimString(props.GeneralQuote)
          : trimString(props.PolicyDetails.Quote),
      CustomersPayments,
      SolidarityUsers,
      payment_desc: props.PolicyDetails.desc,
      productName:
        props.selectedOption === "General Payment"
          ? props.selectedProduct
          : props.PolicyDetails.ProductName,
      RequestType: props.selectedOption,
      expiryDate: expIso,
    };

    try {
      const { isRequestLimitValid, RequestLimitMessage } =
        Validation.RequestLimitValid(props.PolicyDetails.TotalAmount);
      if (!isRequestLimitValid) {
        toast.error(RequestLimitMessage, {
          style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
        });
        return null;
      }

      const { isValid, message } = Validation.isRequired(props.CustomerDetails);
      const { isPolicyDetailsValid, PolicyMessage } = Validation.isPolicyRequired(
        props.PolicyDetails,
        props.selectedOption
      );
      const { isEmailValid, EmailMessage } = Validation.isEmail(
        props.CustomerDetails.email
      );
      const { isPhoneValid, PhoneMessage } = Validation.PhoneNumberValid(
        props.CustomerDetails.phone
      );

      if (!isValid || !isPolicyDetailsValid) {
        if (message) {
          toast.error(message, {
            style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
          });
        }
        if (PolicyMessage) {
          toast.error(PolicyMessage, {
            style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
          });
        }
        return null;
      }

      const errs = [];
      if (!isEmailValid) {
        toast.error(EmailMessage, {
          style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
        });
        errs.push("email");
      }
      if (!isPhoneValid) {
        toast.error(PhoneMessage, {
          style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
        });
        errs.push("phone");
      }
      if (errs.length) return null;

      await CreateOrGetCustomer();

      setBusy(true);
      props.setFormLoading?.(true);

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        errorMsg = await response.text();
        throw new Error("SendRequest failed");
      }

      await response.json();

      // reset UI
      props.setPolicyDetails({});
      props.setCustomerDetails({});
      props.setQuoteNo("");
      props.setFormLoading?.(false);
      setBusy(false);
      props.closeNewRequestModal();
      props.fetchPaymentRequests();
    } catch (error) {
      toast.error(errorMsg || "Something went wrong", {
        style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
      });
      props.setFormLoading?.(false);
      setBusy(false);
      console.error(error);
    }
  };

  const goSubmit = async () => {
    // Guard rails for the special flows
    if (props.selectedOption === "deductible") {
      const res = await GetReserveAmount(
        props.PolicyDetails.Quote,
        "DUDC-RECV",
        props.setFormLoading,
        props.setPolicyDetails,
        props.setCustomerDetails,
        props.setHasReserve,
        toast
      );
      if (res?.statusCode === 200 && res?.Amount !== "0") {
        return SendRequest();
      }
      return;
    }

    if (props.selectedOption === "depreciation") {
      const res = await GetDepReserveAmount(
        props.PolicyDetails.Quote,
        "DEPRE-RECV",
        props.setFormLoading,
        props.setPolicyDetails,
        props.setCustomerDetails,
        props.setHasReserve,
        toast
      );
      if (res?.statusCode === 200) {
        if (res?.Amount === "0") return SendRequest();
      } else if (res?.message === "reserve not found") {
        return SendRequest();
      }
      return;
    }

    // Default
    return SendRequest();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") props.closeNewRequestModal();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
      >
        {/* Header / Steps */}
        <div className="border-b bg-gray-50/70 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              New Payment Request
            </h2>
            <span className="text-xs text-gray-500">
              {props.selectedOption || "—"}
            </span>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => props.setSelectedForm("requestDetails")}
              className={[
                "rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition",
                props.selectedForm === "requestDetails"
                  ? "bg-indigo-600 text-white ring-indigo-600"
                  : "bg-white text-gray-700 hover:bg-gray-50 ring-gray-200",
              ].join(" ")}
            >
              1. Request Details
            </button>
            <button
              onClick={() => props.setSelectedForm("customerDetails")}
              className={[
                "rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition",
                props.selectedForm === "customerDetails"
                  ? "bg-indigo-600 text-white ring-indigo-600"
                  : "bg-white text-gray-700 hover:bg-gray-50 ring-gray-200",
              ].join(" ")}
            >
              2. Customer Details
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div className="max-h-[70vh] overflow-y-auto px-4 sm:px-6 py-4">
          <div className="rounded-xl border border-gray-200 bg-white/80 p-3 sm:p-4">
            {props.selectedForm === "requestDetails"
              ? props.requestDetailsForm
              : props.customerDetailsForm}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="border-t bg-white px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              onClick={props.closeNewRequestModal}
              className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
              disabled={busy}
            >
              Close
            </button>

            {props.selectedForm === "requestDetails" ? (
              <div className="flex justify-end">
                <button
                  onClick={() => props.setSelectedForm("customerDetails")}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                  disabled={busy}
                >
                  Customer Details
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => props.setSelectedForm("requestDetails")}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"
                  disabled={busy}
                >
                  Back
                </button>
                <button
                  onClick={goSubmit}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                  disabled={busy}
                >
                  {busy ? "Submitting..." : "Submit"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestForm;
